import { useCallback, useEffect, useRef, useState } from 'react'
import logger from 'logger'
import links from 'links'
import dayjs from 'date'
import { useIntl } from 'intl'
import type {
  ApplePayConfig,
  ApplePayInstance,
  ApplePayPaymentContact,
  ApplePayPaymentRequest,
  ApplePayShippingContactSelectedEvent,
  ApplePayPaymentAuthorizedEvent,
} from '@recurly/recurly-js'

import type { ApplePayErrorUpdate, ApplePaySelectionUpdate } from '@recurly/recurly-js/lib/apple-pay/native'

import { useCartContext } from 'modules/cart'
import { useUser } from 'modules/user'
import type { AddressInput } from 'typings/graphql'
import { useRecurly } from '../recurly'
import { usePaymentFlow } from '../flow'
import { useInquire } from '../inquire'

import useApplePaySubmit from './useApplePaySubmit'
import messages from './messages'


const createAddressFromContact = (contact: ApplePayPaymentContact): AddressInput => {
  logger.info('[Apple Pay] create address from contact')

  const {
    countryCode, postalCode, locality,
    administrativeArea, addressLines, phoneNumber,
    givenName, familyName,
  } = contact

  let street1 = '', street2 = ''

  if (Array.isArray(addressLines)) {
    street1 = addressLines[0] || ''
    street2 = addressLines[1] || ''
  }

  if (!street1) {
    logger.info('[Apple Pay] street is empty')
  }

  if (!postalCode) {
    logger.error({ contact }, '[Apple Pay] postal code is empty', )
  }

  return {
    firstName: givenName || 'Scentbird',
    lastName: familyName || 'User',
    postalCode,
    country: countryCode,
    city: locality,
    region: administrativeArea,
    street1,
    street2,
    phone: phoneNumber,
  }
}

const SUPPORTED_COUNTRIES = [ 'US', 'CA' ]

const isValidName = (name: string) => /^[A-Za-z\s]+$/.test(name)

const useApplePayCartButton = () => {
  const intl = useIntl()
  const { recurly } = useRecurly()
  const { submit, isProcessing, shippingForm, billingForm } = usePaymentFlow()

  const { orderData, isFetching: isInquireFetching, waitForData, submit: submitInquire } = useInquire()
  const [ isReady, setReady ] = useState(false)
  const applePayRef = useRef<ApplePayInstance>(null)

  const handlePaymentAuthorized = useCallback( (event: ApplePayPaymentAuthorizedEvent): ApplePayErrorUpdate | void => {
    const billingAddressFromWidget = createAddressFromContact(event.payment.billingContact)
    billingForm.setValues(billingAddressFromWidget)

    if (!orderData?.isGiftsOnly) {
      const shippingAddressFromWidget = createAddressFromContact(event.payment.shippingContact)

      if (!isValidName(shippingAddressFromWidget.firstName) || !isValidName(shippingAddressFromWidget.lastName)) {
        return {
          errors: [
            {
              code: 'shippingContactInvalid',
              contactField: 'name',
              message: intl.formatMessage(messages.userError),
            },
          ],
        }
      }

      if (!shippingAddressFromWidget.postalCode) {
        return {
          errors: [
            {
              code: 'shippingContactInvalid',
              contactField: 'postalCode',
              message: intl.formatMessage(messages.postalCodeError),
            },
          ]
        }
      }

      shippingForm.setValues(shippingAddressFromWidget)
    }

    return {} as ApplePayErrorUpdate
  }, [ shippingForm, billingForm, orderData?.isGiftsOnly, intl ])

  const { submitApplePay } = useApplePaySubmit({ applePayRef, submit, payStep: 'Cart' })

  const { modifyCart } = useCartContext()
  const [ _, { isFetching: isModifyCartFetching } ] = modifyCart
  const { country, user } = useUser()

  const handleShippingContactSelected = useCallback(async (event: ApplePayShippingContactSelectedEvent): Promise<ApplePaySelectionUpdate> => {
    logger.info({ address: event.shippingContact }, '[Apple pay] handle shipping contact selected')
    const addressFromWidget = createAddressFromContact(event.shippingContact)

    if (!SUPPORTED_COUNTRIES.includes(addressFromWidget.country)) {
      logger.error(`[Apple Pay] Unsupported country: ${addressFromWidget.country}`)

      return {
        errors: [
          {
            code: 'shippingContactInvalid',
            contactField: 'country',
            message: intl.formatMessage(messages.countryError),
          },
        ],
      }
    }

    // Apple returns a 3-digit postal code for Canada, which is not valid for our validator, so we add 0A0 as the first zone in the city.
    if (addressFromWidget.country === 'CA' && addressFromWidget.postalCode.trim().length === 3) {
      addressFromWidget.postalCode = `${addressFromWidget.postalCode.trim()} 0A0`
    }

    await submitInquire({ address: addressFromWidget, ignoreProcessError: true })

    let updatedOrderData = null

    try {
      updatedOrderData = await waitForData()
    }
    catch (error) {
      if (error?.cause?.purchaseErrorCode === 'INVALID_ADDRESS') {
        return {
          errors: [
            {
              code: 'shippingContactInvalid',
              contactField: 'addressLines',
              message: intl.formatMessage(messages.addressError),
            },
          ],
        }
      }

      return {
        errors: [ { code: 'unknown', message: intl.formatMessage(messages.unexpectedError) } ],
      }
    }

    if (!updatedOrderData) {
      logger.error(`[Apple Pay] order data is null `)

      return {
        errors: [ { code: 'unknown', message: intl.formatMessage(messages.unexpectedError) } ],
      }
    }

    shippingForm.setValues(addressFromWidget)

    return {
      total: intl.formatPrice(updatedOrderData?.total, { withCurrency: false }),
    }
  }, [ submitInquire, shippingForm, intl, waitForData ])

  const buildApplePayConfig = useCallback((): ApplePayConfig => {
    const paymentRequest: ApplePayPaymentRequest = {
      total: {
        label: 'Total',
        amount: intl.formatPrice(orderData?.total, { withCurrency: false }),
      },
      requiredShippingContactFields: orderData?.isGiftsOnly ? [] : [ 'postalAddress' ],
      requiredBillingContactFields: orderData?.isGiftsOnly ? [ 'postalAddress' ] : [],
    }

    // Add subscription info if necessary
    if (orderData.hasSubscription) {
      const subscriptionItem = orderData.items.subscription

      paymentRequest.recurringPaymentRequest = {
        paymentDescription: subscriptionItem.label,
        regularBilling: {
          label: subscriptionItem.label,
          amount: intl.formatPrice(subscriptionItem.price, { withCurrency: false }),
          paymentTiming: 'recurring',
          recurringPaymentIntervalUnit: 'month',
          recurringPaymentIntervalCount: 1,
          // start now. the setting only affects the display in the apple widget
          recurringPaymentStartDate: dayjs().toDate(),
        },
        managementURL: `https://${window.location.host}${links.profile.subscriptionStatus}`,
      }
    }

    const applePayConfig: ApplePayConfig = {
      country,
      currency: intl.currency,
      paymentRequest,
      callbacks: {
        onPaymentAuthorized: handlePaymentAuthorized,
      },
    }

    if (!orderData?.isGiftsOnly) {
      applePayConfig.callbacks.onShippingContactSelected = handleShippingContactSelected
    }

    return applePayConfig
  }, [ country, handleShippingContactSelected, intl, orderData, handlePaymentAuthorized ])

  useEffect(() => {
    setReady(false)

    if (!orderData) {
      return
    }

    if (applePayRef.current) {
      applePayRef.current = null
    }

    try {
      const applePayConfig = buildApplePayConfig()

      if (!orderData.isGiftsOnly && user?.addressInfo?.shipping) {
        const {
          firstName, lastName, phone,
          street1, street2, city,
          region, postalCode, country,
        } = user?.addressInfo?.shipping

        applePayConfig.paymentRequest.shippingContact = {
          givenName: firstName,
          familyName: lastName,
          phoneNumber: phone,
          addressLines: [ street1, street2 ],
          locality: city,
          administrativeArea: region,
          postalCode: postalCode,
          countryCode: country,
        }
      }

      const applePay = recurly.ApplePay(applePayConfig)

      applePay.ready(() => {
        logger.info(`[ApplePay] ready`)
        setReady(true)
      })

      applePayRef.current = applePay
    }
    catch (error) {
      logger.error(error, '[ApplePay] initialization failed')
    }
  }, [ recurly, orderData, buildApplePayConfig, user?.addressInfo?.shipping ])

  return {
    submitApplePay,
    isReady: isReady && !isModifyCartFetching && !isInquireFetching && orderData?.total,
    isProcessing: !isReady || isProcessing || isModifyCartFetching || isInquireFetching || !user,
  }
}

export default useApplePayCartButton
